import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { BACKEND_URI } from "../config/config-vars";

const VAPID_PUBLIC_KEY = 'BDfn1MC37TmEPCT6Ghu-qYNLyCNMtIQUrFkqhoFThJXLTm9Vqs8kUzdgicrYjkCAsvpGLoUiDjjpY8wGi7ao3Fk';
const BASE_URL = `${BACKEND_URI}/api/push/subscribe`;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private newStatsSubject = new Subject<any>();
  private newLocationSubject = new Subject<any>();
  private newPaymentSubject = new Subject<any>();
  private notificationPermission: boolean = false;

  constructor(
    private swPush: SwPush,
    private toastr: ToastrService,
    private http: HttpClient
  ) {
    this.checkNotificationPermission();
  }

  private listenToForegroundPush() {
    this.swPush.messages.subscribe((message: any) => {
      console.log('📥 Push received in foreground:', message);
  
      const title = message.notification?.title || message.title;
      const body = message.notification?.body || message.body;
  
      this.showToastrNotification(title, body);
  
      // 🚀 Real-time routing if already in app
      if (title === 'New Stats' || title === 'New Payment Recorded' || title === 'New Location Added') {
        if (window.location.pathname === '/dashboard') {
          // Soft refresh — your dashboard component should respond to this
          window.dispatchEvent(new Event('refresh-dashboard'));
        } else {
          window.location.href = '/dashboard';
        }
      }
    });
  }  

  private async checkNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      this.notificationPermission = permission === 'granted';
    }
  }

  requestPushSubscription() {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      alert('❌ Push not supported in this browser.');
      return;
    }
  
    if (Notification.permission !== 'granted') {
      alert('❌ Notification permission not granted.');
      return;
    }
  
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array('BDfn1MC37TmEPCT6Ghu-qYNLyCNMtIQUrFkqhoFThJXLTm9Vqs8kUzdgicrYjkCAsvpGLoUiDjjpY8wGi7ao3Fk')
      }).then(sub => {
        console.log('✅ Subscribed to push:', sub);
        this.http.post(BASE_URL, sub).subscribe(() => {
          alert('📬 Notification On');
        });
      }).catch(err => {
        console.error('❌ Notification Subcription Failed:', err);
        alert('❌ Failed: ' + err.message);
      });
    });
  }
  
  // Helper for converting VAPID key
  urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
  
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
  

  notifyNewStats(stats: any) {
    this.newStatsSubject.next(stats);
    this.showNotification('New Stats Available', 'New board statistics have been recorded.');
  }

  notifyNewLocation(location: any) {
    this.newLocationSubject.next(location);
    this.showNotification('New Location Added', `New location ${location.locationName} has been added.`);
    this.toastr.info(`New location added: ${location.locationName}`, 'New Location');
  }

  notifyNewPayment(payment: any) {
    this.newPaymentSubject.next(payment);
    this.showNotification('New Payment Recorded', `Payment of ${payment.amount} has been recorded.`);
    this.toastr.success(`Payment recorded for $${payment.amount.toFixed(2)}`, 'New Payment');
  }

  private showNotification(title: string, body: string) {
    if (this.notificationPermission && 'Notification' in window) {
      try {
        const options: NotificationOptions = {
          body,
          icon: 'assets/images/logo-dark.png',
          badge: 'assets/images/badge.png',
          requireInteraction: true,
          tag: 'fusion-notification'
        } as any; // 👈 bypass TypeScript checking for 'renotify'
        
        (options as any).renotify = true;
        

        const notification = new Notification(title, options);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch (err) {
        console.error('Error showing notification:', err);
        this.showToastrNotification(title, body);
      }
    } else {
      this.showToastrNotification(title, body);
    }
  }

  private showToastrNotification(title: string, body: string) {
    this.toastr.info(body, title, {
      timeOut: 5000,
      positionClass: 'toast-top-right',
      closeButton: true,
      progressBar: true
    });
  }

  onNewStats() {
    return this.newStatsSubject.asObservable();
  }

  onNewLocation() {
    return this.newLocationSubject.asObservable();
  }

  onNewPayment() {
    return this.newPaymentSubject.asObservable();
  }
}
